import {
  FC,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useContext,
} from "react"
import moment from "moment"
import { Button } from "../ui/button"
import { SvgChevronDown } from "../icons/"
import { useLocation } from "react-router-dom"
import { UserCtx } from "../../context/userContext"

type Props = {
  setActiveDate: (date: Date) => void
  meetingDates?: moment.Moment[]
  disablePastDates?: boolean
  changeActiveDateOnMonthChange?: boolean
  dayColor: string
  pickerOpen?: boolean
  setPickerOpen?: any
  activeDate?: Date
  bookingWindow?: any
  prepopulatedDate?: Date | null
  meeting?: any
}

const dayOfWeekStrings = ["S", "M", "T", "W", "T", "F", "S"]

const CustomDayPicker: FC<Props> = ({
  activeDate,
  setActiveDate,
  meetingDates,
  disablePastDates,
  changeActiveDateOnMonthChange,
  dayColor,
  pickerOpen,
  setPickerOpen,
  bookingWindow,
  prepopulatedDate,
  meeting,
}) => {
  const [selectedMonth, setSelectedMonth] = useState<moment.Moment>(moment())
  const [selectedDate, setSelectedDate] = useState<moment.Moment>(moment())
  const { user } = useContext(UserCtx)
  const location = useLocation()
  const { service, contact } = location.state || {}
  const isOwner =
    service?.coach_user_id != null &&
    user?.user_id != null &&
    service.coach_user_id === user.user_id

  const [nowWithBookingWindow, setNowWithBookingWindow] = useState(() =>
    meeting?.isCreator === 0 ? moment().add(bookingWindow, "hours") : moment()
  )

  useEffect(() => {
    setNowWithBookingWindow(
      meeting?.isCreator === 0 ? moment().add(bookingWindow, "hours") : moment()
    )
  }, [bookingWindow, meeting])

  const goForwardOneMonth = useCallback(() => {
    const nextMonth = selectedMonth.clone().add(1, "month")
    if (changeActiveDateOnMonthChange) {
      setActiveDate(nextMonth.toDate())
    }
    setSelectedMonth(nextMonth)
  }, [selectedMonth, changeActiveDateOnMonthChange, setActiveDate])

  const goBackOneMonth = useCallback(() => {
    const previousMonth = selectedMonth.clone().subtract(1, "month")
    if (changeActiveDateOnMonthChange) {
      setActiveDate(previousMonth.toDate())
    }
    setSelectedMonth(previousMonth)
  }, [selectedMonth, changeActiveDateOnMonthChange, setActiveDate])

  const handleSelectDate = useCallback(
    (date: moment.Moment) => {
      if (
        disablePastDates &&
        date.isBefore(nowWithBookingWindow, "day") &&
        meeting?.isCreator === 0
      ) {
        return
      }
      if (date.isAfter(moment().add(199, "days"))) {
        return
      }

      setSelectedDate(date)
      setActiveDate(date.toDate())
    },
    [
      setActiveDate,
      setSelectedDate,
      disablePastDates,
      nowWithBookingWindow,
      meeting,
    ]
  )

  const handleSetToday = () => {
    let targetDate: moment.Moment

    if (meeting?.isCreator === 0 || !isOwner) {
      targetDate = moment().add(bookingWindow, "hours")
    } else {
      targetDate = moment()
    }

    setSelectedMonth(targetDate.clone().startOf("month"))
    setSelectedDate(targetDate)
    setActiveDate(targetDate.toDate())

    if (pickerOpen) {
      setPickerOpen(false)
    }
  }

  const generateDaysInMonth = useCallback((month: moment.Moment) => {
    const currentMonthDates = Array.from(
      { length: month.daysInMonth() },
      (x, i) => moment(month).startOf("month").add(i, "days")
    )
    return currentMonthDates
  }, [])

  const generateActiveDates = useCallback(
    (currentMonthDates: moment.Moment[]) => {
      const numExtraDaysToGenerateAtBeginningOfMonth =
        currentMonthDates[0].day()
      const numExtraDaysToGenerateAtEndOfMonth =
        6 - currentMonthDates[currentMonthDates.length - 1].day()
      const daysInPreviousMonth = currentMonthDates[0]
        .subtract(1, "month")
        .daysInMonth()
      currentMonthDates[0].add(1, "month")

      const dayButtons = []
      for (let i = numExtraDaysToGenerateAtBeginningOfMonth; i > 0; i--) {
        dayButtons.push(
          <button
            onClick={goBackOneMonth}
            key={`prev${i}`}
            className="text-gray py-[6px]"
          >
            {daysInPreviousMonth - i + 1}
          </button>
        )
      }

      currentMonthDates.forEach((day, idx) => {
        const isToday = day.isSame(nowWithBookingWindow, "day")

        let hasMeetingOnDate = false
        if (meetingDates) {
          const match = meetingDates.find((meetingDate) => {
            return day.isSame(meetingDate, "day")
          })
          if (match) {
            hasMeetingOnDate = true
          }
        }

        const isSelectable = !(
          (disablePastDates && day.isBefore(nowWithBookingWindow, "days")) ||
          day.isAfter(moment().add(199, "days"))
        )

        dayButtons.push(
          <button
            onClick={() => handleSelectDate(day)}
            disabled={
              disablePastDates && day.isBefore(nowWithBookingWindow, "day")
            }
            key={`curr${idx}`}
            className={`py-[6px] relative
                        ${
                          day.isSame(selectedDate, "day")
                            ? "text-white rounded-full"
                            : "hover:outline hover:outline-1 hover:outline-offset-[-1px] hover:rounded-full"
                        } 
                        ${
                          disablePastDates &&
                          day.isBefore(nowWithBookingWindow, "days")
                            ? "text-gray rounded-full hover:outline-0"
                            : ""
                        } 
                        ${!isSelectable ? "text-gray cursor-not-allowed" : ""}
                        `}
            style={{
              background: day.isSame(selectedDate, "day") ? dayColor : "",
              outline: dayColor,
            }}
          >
            {day.date()}
            {isToday ? (
              <div
                className={`absolute mx-auto left-0 right-0 bottom-[3px] h-[3px] w-[16px] rounded-full
                            `}
                style={{
                  background: day.isSame(selectedDate, "day")
                    ? "white"
                    : dayColor,
                }}
              ></div>
            ) : (
              <></>
            )}

            {hasMeetingOnDate && !isToday ? (
              <div
                className={`absolute mx-auto left-0 right-0 bottom-[3px] h-[4px] w-[4px] rounded-full
                            `}
                style={{
                  background: day.isSame(selectedDate, "day")
                    ? "white"
                    : dayColor,
                }}
              ></div>
            ) : (
              <></>
            )}
          </button>
        )
      })

      for (let j = 0; j < numExtraDaysToGenerateAtEndOfMonth; j++) {
        dayButtons.push(
          <button
            onClick={goForwardOneMonth}
            key={`next${j}`}
            className="text-gray py-[6px]"
          >
            {j + 1}
          </button>
        )
      }

      return dayButtons
    },
    [
      goForwardOneMonth,
      goBackOneMonth,
      handleSelectDate,
      selectedDate,
      meetingDates,
      disablePastDates,
    ]
  )
  const selectedMonthFormatted = useMemo(
    () => selectedMonth.format("MMMM yyyy"),
    [selectedMonth]
  )

  const activeMonthJSX = useMemo(
    () => generateActiveDates(generateDaysInMonth(selectedMonth)),
    [selectedMonth, generateActiveDates, generateDaysInMonth]
  )

  useEffect(() => {
    if (!prepopulatedDate) {
      if (!activeDate || moment(activeDate).isBefore(moment(), "day")) {
        handleSetToday()
      }
    }
  }, [activeDate])

  useEffect(() => {
    if (prepopulatedDate) {
      const prepopMoment = moment(prepopulatedDate)
      const nowWithWindow = moment().add(bookingWindow, "hours")

      const isDisabled =
        (disablePastDates && prepopMoment.isBefore(nowWithWindow, "day")) ||
        prepopMoment.isAfter(moment().add(199, "days"))

      if (!isDisabled) {
        setSelectedDate(prepopMoment)
        setSelectedMonth(prepopMoment)
        setActiveDate(prepopMoment.toDate())
      } else {
        handleSetToday()
      }
    } else {
      if (meeting) {
        if (meeting.isCreator === 1) {
          // Creator: no booking window
          const today = moment()
          setSelectedDate(today)
          setSelectedMonth(today.clone())
          setActiveDate(today.toDate())
        } else {
          // Not creator: with booking window
          const nowWithBookingWindow = moment().add(bookingWindow, "hours")
          setSelectedDate(nowWithBookingWindow)
          setSelectedMonth(nowWithBookingWindow.clone().startOf("month"))
          setActiveDate(nowWithBookingWindow.toDate())
        }
      } else if (isOwner) {
        // Owner: no booking window
        const today = moment()
        setSelectedDate(today)
        setSelectedMonth(today.clone())
        setActiveDate(today.toDate())
      } else {
        // No meeting at all and not owner: treat as not creator (apply booking window)
        const nowWithBookingWindow = moment().add(bookingWindow, "hours")
        setSelectedDate(nowWithBookingWindow)
        setSelectedMonth(nowWithBookingWindow)
        setActiveDate(nowWithBookingWindow.toDate())
      }
    }
  }, [prepopulatedDate, bookingWindow, disablePastDates, meeting])

  return (
    <div className="w-full">
      <h4 className="flex justify-between font-semibold text-[17px] -mt-4 px-2">
        {selectedMonthFormatted}
        {/* Commenting out Next Available button for now - need to fix the function */}
        {/* <Button
          onClick={() => handleSetToday()}
          variant="outline"
          className="h-[24px] px-2 text-xs border border-gray text-black"
        >
          Next Available
        </Button> */}
      </h4>

      <div className="flex items-center justify-between mb-[10px] px-4 text-gray text-sm">
        <button className="flex items-center" onClick={goBackOneMonth}>
          <span className="rotate-90 text-sm">
            <SvgChevronDown />
          </span>
          {selectedMonth.clone().subtract(1, "month").format("MMM")}
        </button>
        <button className="flex items-center" onClick={goForwardOneMonth}>
          {selectedMonth.clone().add(1, "month").format("MMM")}
          <span className="-rotate-90 text-sm">
            <SvgChevronDown />
          </span>
        </button>
      </div>

      <div className="grid grid-cols-7 text-center mb-[5px]">
        {dayOfWeekStrings.map((dayString, idx) => {
          return (
            <h6 key={idx} className="text-graySlate text-base">
              {dayString}
            </h6>
          )
        })}
      </div>
      {pickerOpen ? (
        <div
          onClick={() => setPickerOpen(!pickerOpen)}
          className="grid grid-cols-7 gap-y-[2px] text-center text-base font-semibold"
        >
          {activeMonthJSX.map((date) => date)}
        </div>
      ) : (
        <div className="h-[3/4] grid grid-cols-7 gap-y-[2px] text-center text-sm font-semibold">
          {activeMonthJSX.map((date) => date)}
        </div>
      )}
    </div>
  )
}

export default CustomDayPicker
